import React, { useCallback, useEffect, useState } from 'react';
import { T } from '@tolgee/react';
import TabButton from '../../OrderDetailsTabButton/OrderDetailsTabButton';
import { TabConfig } from '../../OrderDetailsTabButton/OrderDetailsTabButton.interface';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { Calendar } from '../../../assets/Calendar';
import dayjs from 'dayjs';
import { Contract } from '../../../services/contracts.service';
import GeneralTab from './GeneralTab';
import OrdersTab from './OrdersTab';
import ModalEditContract from './ModalEditContract';
import ModalCancelContract from './ModalCancelContract';
import { cn } from '../../../lib/utils';
import { OrderTarget } from '../../OrdersTable/interfaces/OrderStatus.interface';
import { ContractStatus } from '../interfaces/Contract.interface';
import { ContractsRedSmall } from '../../../assets/Contracts/ContractsRedSmall';
import EventsTab from './EventsTab';

const TAB_CONFIG: Record<string, TabConfig> = {
	overview: {
		icon: 'Document Text',
		label: 'tabButton.general',
		translatedLabel: 'General',
	},
	orders: {
		icon: 'Folder',
		label: 'tabButton.orders',
		translatedLabel: 'Orders',
	},
	events: {
		icon: 'Bell',
		label: 'tabButton.events',
		translatedLabel: 'Events',
	},
} as const;

interface ContractsDetailsProps {
	contract: Contract;
	role: OrderTarget;
	onOrderClick?: (orderId: number) => void;
	onContractUpdate?: (updated: Contract) => void;
	onCompanyClick?: (companyId: number) => void;
}

const ContractsDetails: React.FC<ContractsDetailsProps> = ({
	contract,
	role,
	onOrderClick,
	onContractUpdate,
	onCompanyClick,
}) => {
	const [currentTab, setCurrentTab] =
		useState<keyof typeof TAB_CONFIG>('overview');
	const [openEdit, setOpenEdit] = useState<boolean>(false);
	const [openCancel, setOpenCancel] = useState<boolean>(false);
	const [current, setCurrent] = useState<Contract>(contract);
	const [editMode, setEditMode] = useState<'edit' | 'resume'>('edit');

	const handleContractUpdated = useCallback(
		(updated: Contract) => {
			setCurrent(updated);
			onContractUpdate?.(updated);
		},
		[onContractUpdate],
	);

	useEffect(() => {
		setCurrent(contract);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contract?.id]);

	return (
		<>
			<div className="fixed min-h-full top-0 right-0 w-[716px] border border-solid border-gray-10 rounded-l-2xl bg-[#FAFAFA] shadow-order-details z-[100] h-screen box-border transition">
				<div className="flex h-[calc(100%-28px)] min-h-0 flex-col gap-[14px] py-[14px]">
					<nav className="flex gap-[3px] px-[40px]">
						{Object.keys(TAB_CONFIG).map((tab) => (
							<TabButton
								key={tab}
								tab={tab}
								isActive={currentTab === tab}
								config={TAB_CONFIG[tab]}
								notificationCount={
									tab === 'events' ? contract.notifications?.count : undefined
								}
								onClick={setCurrentTab}
								inContracts={true}
							/>
						))}
					</nav>

					<div className="flex items-center justify-between border-0 border-t border-b border-solid border-gray-10 px-[40px]">
						{currentTab === 'overview' ? (
							<div className="h-[16px] flex gap-5 py-[18px] items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px]">
										<T keyName="adminContracts.created">Created:</T>
									</p>
								</SypacText>

								<div className="h-[16px] flex items-center gap-3">
									<Calendar />
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
											{dayjs(contract.createdAt).format('DD.MM.YYYY')}
										</p>
									</SypacText>
									{/*
										<div className="w-[2px] h-[2px] bg-gray-40 rounded-full" />
										<SypacText variant="body-regular-medium">
											<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
												John Doe
											</p>
										</SypacText>
									*/}
								</div>
							</div>
						) : (
							<div className="h-[16px] flex gap-5 py-[18px] items-center">
								<SypacText variant="body-regular-medium">
									<p className="text-[14px] leading-[20px] text-gray-40">
										<T keyName="contractsTable.ContractExpirationDate">
											Contract expiration date:
										</T>
									</p>
								</SypacText>

								<div className="h-[16px] flex items-center gap-3">
									<Calendar />
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 text-[14px] leading-[16px] mt-[2px]">
											{dayjs(contract.expiresAt).format('DD.MM.YYYY')}
										</p>
									</SypacText>
								</div>
							</div>
						)}

						{role === 'admin' && contract.status !== 'canceled' ? (
							<div className="flex items-center gap-[18px]">
								<SypacButton variant="subTitle">
									<button
										type="button"
										className="w-fit flex justify-center items-center p-0 m-0 border-0 bg-transparent"
										onClick={() => setOpenCancel(true)}
									>
										<SypacText variant="body-regular-medium">
											<p className="text-red underline underline-offset-[3.5px] transition hover:text-red/80 hover:decoration-red/60">
												<T keyName="adminContracts.cancelContract">
													Cancel contract
												</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
								<SypacButton variant="subTitle">
									<button
										type="button"
										className="w-fit flex justify-center items-center p-0 m-0 border-0 bg-transparent"
										onClick={() => {
											setEditMode('edit');
											setOpenEdit(true);
										}}
									>
										<SypacText variant="body-regular-medium">
											<p className="text-cornflower-blue underline underline-offset-[3.5px] transition hover:text-cornflower-blue/80 hover:decoration-cornflower-blue/60">
												<T keyName="adminContracts.editContract">
													Edit contract
												</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
							</div>
						) : null}
					</div>

					{role === 'admin' && contract.status === ContractStatus.canceled && (
						<div className="flex items-start gap-3 px-[40px] mt-[6px]">
							<ContractsRedSmall />

							<div className="w-full flex flex-col gap-3">
								<div className="flex items-center justify-between">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 text-[24px] leading-[32px]">
											<T keyName="contractsDetails.contractCanceled">
												Contract canceled
											</T>
										</p>
									</SypacText>

									<SypacButton variant="subTitle">
										<button
											type="button"
											className="w-fit flex justify-center items-center p-0 m-0 border-0 bg-transparent"
											onClick={() => {
												setEditMode('resume');
												setOpenEdit(true);
											}}
										>
											<SypacText variant="body-regular-medium">
												<p className="text-cornflower-blue underline underline-offset-[3.5px] transition hover:text-cornflower-blue/80 hover:decoration-cornflower-blue/60">
													<T keyName="modalSelfPickup.resumeContract">
														Resume contract
													</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
								</div>

								<SypacText variant="body-regular-medium">
									<p className="text-gray-40 text-[14px] leading-[16px]">
										<T keyName="contractsDetails.contractHasBeenCanceled">
											This contract has been canceled. Any remaining rights and
											obligations of the parties are governed solely by the
											cancellation terms of their agreement.
										</T>
									</p>
								</SypacText>
							</div>
						</div>
					)}

					<div
						className={cn(
							'flex-1 min-h-0 flex flex-col',
							current?.status === ContractStatus.canceled && 'opacity-50',
						)}
					>
						{currentTab === 'overview' && (
							<GeneralTab
								contract={current}
								role={role}
								onCompanyClick={onCompanyClick}
							/>
						)}

						{currentTab === 'orders' && (
							<OrdersTab
								contract={current}
								role={role}
								onOrderClick={onOrderClick}
							/>
						)}

						{currentTab === 'events' && (
							<EventsTab contract={current} role={role} />
						)}
					</div>
				</div>
			</div>
			<ModalEditContract
				isOpen={openEdit}
				onClose={() => setOpenEdit(false)}
				contract={current}
				onUpdated={handleContractUpdated}
				mode={editMode}
			/>
			<ModalCancelContract
				contract={current}
				isOpen={openCancel}
				onClose={() => setOpenCancel(false)}
				onUpdated={handleContractUpdated}
			/>
		</>
	);
};

export default ContractsDetails;
